import { useEffect, useState } from 'react';
import { Lightbulb } from 'shared/icons';
import api from '../../services/api';
import { FormatInt, FormatMoney } from '../../utils/format';
import ErrorCard from '../ui/ErrorCard';
import Input from '../ui/Input';
import Select from '../ui/Select';
import StandardTable from '../ui/StandardTable';
import { CampaignTypeLabel, DASH, IntelLoading, ItemCell, MlbLink } from './intelligenceShared';

/**
 * OPORTUNIDADES (Fase 4) — participações com status `candidate` em campanha
 * JÁ INICIADA, de anúncios que hoje estão SEM campanha ativa, ranqueadas por
 * vendas dos últimos N dias. É a lista "um clique de aceitar": o que mais
 * vende e ficaria coberto só com o opt-in que o ML já ofereceu.
 *
 * O ranking é do SERVIDOR (units_sold desc) — a tabela não reordena por
 * default para não desfazer a priorização que dá sentido à lista.
 */

const DAYS_OPTIONS = [
    { value: 7, label: 'Últimos 7 dias' },
    { value: 15, label: 'Últimos 15 dias' },
    { value: 30, label: 'Últimos 30 dias' },
];

/** Teto do wire — o backend corta em 50; acima disso viria paginação real. */
const OPPORTUNITIES_LIMIT = 50;

const COLUMNS = [
    {
        key: 'units_sold',
        label: 'Vendas',
        align: 'center',
        getValue: (row) => Number(row.units_sold || 0),
        render: (row) => (
            <span className="inline-flex rounded-full bg-success-100 px-2.5 py-0.5 text-sm font-bold tabular-nums text-success-800 dark:bg-success-900/40 dark:text-success-300">
                {FormatInt(row.units_sold || 0)}
            </span>
        ),
    },
    {
        key: 'item',
        label: 'Anúncio',
        getValue: (row) => `${row.title || ''} ${row.sku || ''}`,
        render: (row) => <ItemCell title={row.title} sku={row.sku} />,
    },
    {
        key: 'item_id',
        label: 'MLB',
        getValue: (row) => row.item_id,
        render: (row) => <MlbLink itemId={row.item_id} />,
    },
    {
        key: 'price',
        label: 'Preço atual',
        align: 'right',
        // Número cru p/ sort/CSV — a string formatada ('R$ …') ordenaria
        // lexicograficamente; a formatação é só do render.
        getValue: (row) => Number(row.price ?? -Infinity),
        render: (row) => <span className="tabular-nums">{FormatMoney(row.price)}</span>,
    },
    {
        key: 'campaign',
        label: 'Campanha candidata',
        getValue: (row) => `${row.campaign_name || row.campaign_id || ''} ${CampaignTypeLabel(row.campaign_type)}`,
        render: (row) => (
            <div className="min-w-0">
                <p className="truncate text-sm font-medium text-black dark:text-white">
                    {row.campaign_name || row.campaign_id || DASH}
                </p>
                <p className="mt-0.5 text-xs text-mono-500">{CampaignTypeLabel(row.campaign_type)}</p>
            </div>
        ),
    },
    {
        key: 'suggested_discounted_price',
        label: 'Preço sugerido',
        align: 'right',
        // Sem sugestão do ML vira -Infinity: mantém o sort numérico estável e
        // afunda os '—' junto dos menores preços em vez de misturar tipos.
        getValue: (row) =>
            row.suggested_discounted_price === null || row.suggested_discounted_price === undefined
                ? -Infinity
                : Number(row.suggested_discounted_price),
        render: (row) =>
            // Destaque SÓ quando o ML mandou preço sugerido — ausência é '—',
            // nunca um número estimado no front (regra do monitor).
            row.suggested_discounted_price === null || row.suggested_discounted_price === undefined ? (
                <span className="text-mono-400">{DASH}</span>
            ) : (
                <span className="font-bold tabular-nums text-success-600 dark:text-success-400">
                    {FormatMoney(row.suggested_discounted_price)}
                </span>
            ),
    },
];

const Opportunities = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [days, setDays] = useState(7);
    // min_units digitado fica num estado próprio e só vira filtro no blur/
    // Enter: refazer a consulta a cada tecla dispararia um request por dígito.
    const [minUnitsInput, setMinUnitsInput] = useState('1');
    const [minUnits, setMinUnits] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const payload = await api.getMarketIntelligenceOpportunities({
                    days,
                    min_units: minUnits,
                    limit: OPPORTUNITIES_LIMIT,
                });
                if (!cancelled) {
                    const list = Array.isArray(payload?.items) ? payload.items : [];
                    setRows(list);
                    setTotal(Number(payload?.total ?? list.length));
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || 'Não foi possível carregar as oportunidades.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [days, minUnits, refreshTick]);

    const applyMinUnits = () => {
        // Vazio/inválido volta ao mínimo do wire (1) — filtro nunca fica NaN.
        const parsed = Math.max(1, Math.trunc(Number(minUnitsInput) || 1));
        setMinUnitsInput(String(parsed));
        setMinUnits(parsed);
    };

    if (error) {
        return (
            <ErrorCard
                title="Erro ao carregar oportunidades"
                message={error}
                action="Tentar novamente"
                actionFn={() => setRefreshTick((tick) => tick + 1)}
            />
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-xl border border-info-200 bg-info-50 p-4 dark:border-info-800 dark:bg-info-900/20">
                <Lightbulb size={20} className="mt-0.5 shrink-0 text-info-600 dark:text-info-400" />
                <p className="text-sm text-info-800 dark:text-info-200">
                    Anúncios <strong>sem campanha ativa</strong> que o ML já convidou (status candidato) para uma
                    campanha <strong>em andamento</strong> — ordenados por vendas do período. São os opt-ins de maior
                    impacto: um aceite e o anúncio mais vendido entra em campanha.
                </p>
            </div>

            <div className="flex flex-wrap items-end gap-3">
                <div className="w-48">
                    <Select label="Período de vendas" value={days} onChange={(event) => setDays(Number(event.target.value))}>
                        {DAYS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </div>
                <div className="w-40">
                    <Input
                        label="Mínimo de vendas"
                        type="number"
                        min="1"
                        value={minUnitsInput}
                        onChange={(event) => setMinUnitsInput(event.target.value)}
                        onBlur={applyMinUnits}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                applyMinUnits();
                            }
                        }}
                    />
                </div>
                {total > rows.length ? (
                    <p className="pb-2 text-xs text-mono-500">
                        Mostrando os {FormatInt(rows.length)} primeiros de {FormatInt(total)} — aperte o filtro de
                        vendas para focar o topo.
                    </p>
                ) : null}
            </div>

            {loading ? <IntelLoading text="Carregando oportunidades…" /> : null}
            {/* Sem linhas E carregando = primeira consulta em voo: a tabela
                fica fora do DOM para o emptyMessage definitivo ("nenhuma
                oportunidade") não aparecer antes de a resposta existir. Em
                refetch com dados, a tabela anterior segue visível sob o
                indicador acima. */}
            {loading && rows.length === 0 ? null : (
                <StandardTable
                    rows={rows}
                    columns={COLUMNS}
                    // Um item pode ser candidato em MAIS de uma campanha started —
                    // a linha é o PAR item×campanha, não o item sozinho.
                    rowKey={(row) => `${row.item_id}:${row.campaign_id}`}
                    countLabel="oportunidades"
                    emptyMessage="Nenhuma oportunidade no recorte — nenhum candidato de campanha iniciada em item sem campanha."
                    exportFileName="oportunidades-campanha"
                    searchPlaceholder="Buscar anúncio, SKU ou campanha..."
                />
            )}
        </div>
    );
};

export default Opportunities;
